import React from 'react'
import { toast } from 'sonner'
import ProfileAvatar from '@/assets/profile/ProfileAvatar.svg'
import AIAvatar from '@/assets/report/AiStatic.svg'
// import { useGetPublicProfileQuery } from '@/api/userPublicApiSlice'

type Flag =
  | string
  | {
      type: string
      color: string
    }

export interface QuestionAsked {
  question: string
  candidateResponse?: string
  rating?: number | string
  flag?: Flag
  remarks?: string
  idealResponse?:
    | string
    | {
        description: string
        bestResponse?: string
      }
}

interface BaseMessage {
  _id: string
  role: 'USER' | 'AI' | string
  isInstructionMessage?: boolean
  isToolCall?: boolean
}

interface SingleMessage extends BaseMessage {
  message: string
  isCodeSnippet?: boolean
}

interface GroupedMessage extends BaseMessage {
  message: string[]
  isCodeSnippet: boolean[]
}

export type Message = SingleMessage | GroupedMessage

const AiAvatar: React.FC = () => (
  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-semibold text-gray-700">
    AI
  </div>
)

const MessageRenderer: React.FC<{
  message: string
  isCodeSnippet?: boolean
}> = ({ message, isCodeSnippet }) => {
  const handleCopyClick = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Code copied to clipboard')
    } catch (err) {
      toast.error('Failed to copy code')
      console.error('Failed to copy:', err)
    }
  }

  if (isCodeSnippet) {
    return (
      <div className="my-2 w-full">
        <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-t-md border border-b-0 border-gray-200 flex justify-between items-center">
          <span className="text-xs">Code Snippet</span>
          <button
            onClick={() => handleCopyClick(message)}
            className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800 flex items-center gap-1"
            title="Copy code"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            Copy
          </button>
        </div>
        <pre className="bg-gray-50 border border-gray-200 rounded-b-md p-3 overflow-x-auto sm:p-2">
          <code className="text-xs font-mono text-gray-800 whitespace-pre-wrap">{message}</code>
        </pre>
      </div>
    )
  }

  return <div className="whitespace-pre-wrap text-gray-700">{message}</div>
}

interface PracticeTranscriptProps {
  questionsAsked: QuestionAsked[]
  messages: Message[]
  username: string
  isSharedReport: boolean
  hideControls?: boolean
  flagFilter?: string
  onFlagFilterChange?: (value: string) => void
  searchTerm?: string
  onSearchTermChange?: (value: string) => void
  filterOptionsOverride?: string[]
}

export const buildFlagOptions = (qs: QuestionAsked[]) => {
  const flags = new Set<string>()
  qs.forEach((q) => {
    const hasIdeal =
      typeof q.idealResponse === 'string'
        ? q.idealResponse.trim().length > 0
        : Boolean(q.idealResponse?.description)
    if (!hasIdeal) return
    if (!q.flag) return
    const type = typeof q.flag === 'object' ? q.flag.type : q.flag
    if (type) flags.add(type)
  })
  return Array.from(flags).sort()
}

export const TranscriptFiltersBar: React.FC<{
  flagFilter: string
  onFlagFilterChange: (value: string) => void
  filterOptions: string[]
  searchTerm: string
  onSearchTermChange: (value: string) => void
  showFlagSelector?: boolean
}> = ({
  flagFilter,
  onFlagFilterChange,
  filterOptions,
  searchTerm,
  onSearchTermChange,
  showFlagSelector ,
}) => (
  <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
    <div className="flex items-center justify-end gap-3 w-full sm:w-auto sm:flex-row sm:justify-end">
      {showFlagSelector ? (
        <div className="relative">
          <select
            value={flagFilter}
            onChange={(e) => onFlagFilterChange(e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200 min-w-[140px]"
          >
            {filterOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
            ▾
          </span>
        </div>
      ) : null}

      <div
        className={`relative w-full ${
          showFlagSelector ? 'sm:w-80 flex-1 max-w-xl' : 'max-w-full'
        }`}
      >
        <input
          type="text"
          placeholder="Search messages, issues…"
          className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z"
          />
        </svg>
      </div>
    </div>
  </div>
)

const Transcript: React.FC<PracticeTranscriptProps> = ({
  questionsAsked,
  messages,
  username,
  isSharedReport,
  hideControls = false,
  flagFilter,
  onFlagFilterChange,
  searchTerm,
  onSearchTermChange,
  filterOptionsOverride,
}) => {
  // const { data: profile } = useGetPublicProfileQuery({ username }, { skip: !username })
  const [internalFlagFilter, setInternalFlagFilter] = React.useState<string>('All')
  const [internalSearchTerm, setInternalSearchTerm] = React.useState<string>('')

  const currentFlagFilter = flagFilter ?? internalFlagFilter
  const currentSearchTerm = searchTerm ?? internalSearchTerm
  const handleFlagChange = onFlagFilterChange ?? setInternalFlagFilter
  const handleSearchChange = onSearchTermChange ?? setInternalSearchTerm

  const groupedMessages = React.useMemo(() => {
    return messages.reduce<Message[]>((acc, curr) => {
      if (curr.role === 'AI') {
        const prevMessage = acc[acc.length - 1]
        if (prevMessage && prevMessage.role === 'AI') {
          const newAcc = acc.slice(0, -1)
          const prevMessageArray = Array.isArray(prevMessage.message)
            ? prevMessage.message
            : [prevMessage.message]
          const messagePairs = [
            ...prevMessageArray.map((msg: string, i: number) => ({
              text: msg,
              isCodeSnippet: Array.isArray((prevMessage as GroupedMessage).isCodeSnippet)
                ? (prevMessage as GroupedMessage).isCodeSnippet[i]
                : !!(prevMessage as SingleMessage).isCodeSnippet,
            })),
            {
              text: (curr as SingleMessage).message,
              isCodeSnippet: !!(curr as SingleMessage).isCodeSnippet,
            },
          ]

          const groupedMessage: GroupedMessage = {
            _id: prevMessage._id,
            role: prevMessage.role,
            isInstructionMessage: prevMessage.isInstructionMessage,
            isToolCall: prevMessage.isToolCall,
            message: messagePairs.map((m) => m.text),
            isCodeSnippet: messagePairs.map((m) => m.isCodeSnippet ?? false) as boolean[],
          }

          return [...newAcc, groupedMessage]
        }
      }

      if (curr.role === 'AI') {
        const groupedMessage: GroupedMessage = {
          _id: curr._id,
          role: curr.role,
          isInstructionMessage: curr.isInstructionMessage,
          isToolCall: curr.isToolCall,
          message: [(curr as SingleMessage).message],
          isCodeSnippet: [!!(curr as SingleMessage).isCodeSnippet],
        }
        return [...acc, groupedMessage]
      }

      const singleMessage: SingleMessage = {
        _id: curr._id,
        role: curr.role,
        isInstructionMessage: curr.isInstructionMessage,
        isToolCall: curr.isToolCall,
        message: (curr as SingleMessage).message,
        isCodeSnippet: !!(curr as SingleMessage).isCodeSnippet,
      }
      return [...acc, singleMessage]
    }, [])
  }, [messages])

  const responseMap = React.useMemo(() => {
    const map = new Map<string, QuestionAsked>()
    questionsAsked.forEach((q) => {
      if (q.candidateResponse) {
        const trimmed = q.candidateResponse.trim()
        map.set(trimmed, q)
        map.set(trimmed.toLowerCase(), q)
      }
    })
    return map
  }, [questionsAsked])

  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => {
    const initialExpandedIds = new Set<string>()
    const userMessages = messages.filter((m) => m.role === 'USER')
    userMessages.forEach((message, userIndex) => {
      if (userIndex < questionsAsked.length) {
        const question = questionsAsked[userIndex]
        if (question && question.flag) {
          initialExpandedIds.add(message._id)
        }
      }
    })
    return initialExpandedIds
  })

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const matchesFilter = (question?: QuestionAsked, messageText?: string) => {
    if (currentFlagFilter !== 'All') {
      const type =
        question && typeof question.flag === 'object'
          ? question.flag.type
          : typeof question?.flag === 'string'
            ? question.flag
            : undefined
      if (!type || type !== currentFlagFilter) return false
    }
    const needle = currentSearchTerm.trim().toLowerCase()
    if (!needle) return true
    const haystack = [
      messageText ?? '',
      question?.question ?? '',
      question?.remarks ?? '',
      typeof question?.idealResponse === 'string'
        ? question.idealResponse
        : question?.idealResponse?.description ?? '',
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(needle)
  }

  const availableFlagOptions = React.useMemo(
    () => buildFlagOptions(questionsAsked),
    [questionsAsked],
  )

  const flagOptions = (filterOptionsOverride ?? availableFlagOptions).filter((opt) => Boolean(opt?.trim?.() ?? opt))
  const hasFlags = flagOptions.length > 0
  const filterOptions = hasFlags ? ['All', ...flagOptions] : []
  const showFlagSelector = hasFlags

  const resolveQuestionForIndex = (message: Message, messageIndex: number): QuestionAsked | undefined => {
    const messageText =
      typeof message.message === 'string'
        ? message.message
        : Array.isArray(message.message)
          ? message.message[0]
          : ''
    const trimmed = messageText?.trim() ?? ''
    let question = responseMap.get(trimmed) || responseMap.get(trimmed.toLowerCase())
    if (!question && questionsAsked.length > 0 && message.role === 'USER') {
      const userMessagesBeforeThis = groupedMessages.slice(0, messageIndex + 1).filter((m) => m.role === 'USER')
      const userMessageIndex = userMessagesBeforeThis.length - 1
      if (userMessageIndex >= 0 && userMessageIndex < questionsAsked.length) {
        question = questionsAsked[userMessageIndex]
      }
    }
    return question
  }

  const messageTextForSearch = (message: Message): string => {
    if (typeof message.message === 'string') return message.message
    if (Array.isArray(message.message)) return message.message.join(' ')
    return ''
  }

  const filteredMessages = groupedMessages.filter((message, messageIndex) => {
    const question = resolveQuestionForIndex(message, messageIndex)
    const messageText = messageTextForSearch(message)
    return matchesFilter(question, messageText)
  })

  return (
    <div className="space-y-6 w-full">
      {!hideControls && (
        <TranscriptFiltersBar
          flagFilter={currentFlagFilter}
          onFlagFilterChange={handleFlagChange}
          filterOptions={filterOptions}
          searchTerm={currentSearchTerm}
          onSearchTermChange={handleSearchChange}
          showFlagSelector={showFlagSelector}
        />
      )}

      {messages.length === 0 && filteredMessages.length === 0 && (
        <div className="text-gray-500 text-center py-8">No messages to display.</div>
      )}

      {messages.length > 0 && filteredMessages.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center text-gray-500">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z"
              />
            </svg>
          </div>
          <p className="text-sm">No matches found for this search/filter.</p>
          <p className="text-xs text-gray-400 mt-1">Try a different keyword or clear filters.</p>
        </div>
      )}

      <div className="space-y-6">
        {filteredMessages.map((message, messageIndex) => {
          let question: QuestionAsked | undefined
          if (message.role === 'USER') {
            const messageText =
              typeof message.message === 'string'
                ? message.message
                : Array.isArray(message.message)
                  ? message.message[0]
                  : ''
            const trimmed = messageText?.trim() ?? ''
            question = responseMap.get(trimmed) || responseMap.get(trimmed.toLowerCase())
            if (!question && questionsAsked.length > 0) {
              const userMessagesBeforeThis = groupedMessages
                .slice(0, messageIndex + 1)
                .filter((m) => m.role === 'USER')
              const userMessageIndex = userMessagesBeforeThis.length - 1
              if (userMessageIndex >= 0 && userMessageIndex < questionsAsked.length) {
                question = questionsAsked[userMessageIndex]
              }
            }
          }

          const isFlagged = !!question
          const isExpanded = expandedIds.has(message._id)

          return (
            <div key={message._id} className="space-y-2">
              <div
                className={`flex items-start gap-4 px-3 sm:px-1 sm:gap-3 ${
                  message.role === 'AI' ? ' bg-background-grey rounded-[6px] py-3' : ''
                }`}
              >
                <div className="shrink-0">
                  {message.role === 'USER' ? (
                    <img
                      src={ProfileAvatar}
                      alt="User"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <img src={AIAvatar} alt="AI" className="w-5 h-5 rounded-full object-cover" />
                  )}
                </div>

                <div className="flex-1">
                  <div
                    className={`inline-block ${
                      message.role === 'AI' ? 'rounded-2xl rounded-tl-none' : ''
                    }`}
                  >
                    <div
                      className={`text-body2 sm:text-[13px] md:text-[13px] ${
                        message.role === 'USER' ? 'text-grey-5' : 'text-grey-10'
                      }`}
                    >
                      {'message' in message && Array.isArray(message.message) ? (
                        (message as GroupedMessage).message.map((msg, idx) => (
                          <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
                            <MessageRenderer
                              message={msg}
                              isCodeSnippet={(message as GroupedMessage).isCodeSnippet[idx]}
                            />
                          </div>
                        ))
                      ) : (
                        <MessageRenderer
                          message={(message as SingleMessage).message}
                          isCodeSnippet={(message as SingleMessage).isCodeSnippet}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {message.role === 'USER' && isFlagged && !isExpanded && !isSharedReport && (
                <div className="ml-[52px] sm:ml-9">
                  <button
                    onClick={() => handleToggleExpand(message._id)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <span
                      className={`flex items-center gap-[6px] px-2 py-1 rounded text-xs font-medium ${
                        question &&
                        (typeof question.flag === 'string'
                          ? 'bg-[#EDFFF5]'
                          : (question.flag as { type: string; color: string })?.color === '#10B754'
                            ? 'bg-[#EDFFF5] text-[#10B754] border border-[#10B754]/20'
                            : (question.flag as { type: string; color: string })?.color === '#B79035'
                              ? 'bg-[#FFFAED] text-[#B79035] border border-[#B79035]/20'
                              : (question.flag as { type: string; color: string })?.color === '#B77235'
                                ? 'bg-[#FFF5ED] text-[#B77235] border border-[#B77235]/20'
                                : 'bg-[#FFEDED] text-[#700108] border border-[#700108]/20  ')
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                      >
                        <mask
                          id={`mask0_flag_${message._id}`}
                          style={{ maskType: 'alpha' }}
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="21"
                          height="21"
                        >
                          <rect x="0.5" y="0.599121" width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask={`url(#mask0_flag_${message._id})`}>
                          <path
                            d="M6 11.5991V17.8491C6 18.0616 5.92854 18.2397 5.78563 18.3835C5.64271 18.5272 5.46563 18.5991 5.25438 18.5991C5.04313 18.5991 4.86458 18.5272 4.71875 18.3835C4.57292 18.2397 4.5 18.0616 4.5 17.8491V4.34912C4.5 4.13662 4.57188 3.9585 4.71563 3.81475C4.85938 3.671 5.0375 3.59912 5.25 3.59912H15.6458C15.794 3.59912 15.9253 3.63579 16.0398 3.70912C16.1542 3.78246 16.2451 3.87773 16.3125 3.99495C16.3819 4.10607 16.4132 4.22759 16.4062 4.35954C16.3993 4.49148 16.3611 4.61995 16.2917 4.74495L14.5 7.59912L16.2917 10.4533C16.3611 10.5783 16.3993 10.7068 16.4062 10.8387C16.4132 10.9706 16.3819 11.0922 16.3125 11.2033C16.2431 11.3144 16.1514 11.4081 16.0375 11.4845C15.9237 11.5609 15.7932 11.5991 15.6458 11.5991H6Z"
                            fill={
                              question && typeof question.flag === 'object' && question.flag?.color
                                ? question.flag.color
                                : '#700108'
                            }
                          />
                        </g>
                      </svg>
                      {question && (typeof question.flag === 'string' ? question.flag : question.flag?.type)}
                    </span>
                    <span>View more</span>
                  </button>
                </div>
              )}

              {isFlagged && isExpanded && question && !isSharedReport && (
                <div className="ml-[52px] space-y-3 sm:ml-9">
                  <div
                    className="flex items-center gap-2 mb-3"
                    onClick={() => handleToggleExpand(message._id)}
                  >
                    <span
                      className={`flex items-center gap-[6px] px-2 py-1 rounded text-xs font-medium ${
                        question &&
                        (typeof question.flag === 'string'
                          ? 'bg-[#EDFFF5]'
                          : (question.flag as { type: string; color: string })?.color === '#10B754'
                            ? 'bg-[#EDFFF5] text-[#10B754] border border-[#10B754]/20'
                            : (question.flag as { type: string; color: string })?.color === '#B79035'
                              ? 'bg-[#FFFAED] text-[#B79035] border border-[#B79035]/20'
                              : (question.flag as { type: string; color: string })?.color === '#B77235'
                                ? 'bg-[#FFF5ED] text-[#B77235] border border-[#B77235]/20'
                                : 'bg-[#FFEDED] text-[#700108] border border-[#700108]/20  ')
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                      >
                        <mask
                          id={`mask0_flag_expanded_${message._id}`}
                          style={{ maskType: 'alpha' }}
                          maskUnits="userSpaceOnUse"
                          x="0"
                          y="0"
                          width="21"
                          height="21"
                        >
                          <rect x="0.5" y="0.599121" width="20" height="20" fill="#D9D9D9" />
                        </mask>
                        <g mask={`url(#mask0_flag_expanded_${message._id})`}>
                          <path
                            d="M6 11.5991V17.8491C6 18.0616 5.92854 18.2397 5.78563 18.3835C5.64271 18.5272 5.46563 18.5991 5.25438 18.5991C5.04313 18.5991 4.86458 18.5272 4.71875 18.3835C4.57292 18.2397 4.5 18.0616 4.5 17.8491V4.34912C4.5 4.13662 4.57188 3.9585 4.71563 3.81475C4.85938 3.671 5.0375 3.59912 5.25 3.59912H15.6458C15.794 3.59912 15.9253 3.63579 16.0398 3.70912C16.1542 3.78246 16.2451 3.87773 16.3125 3.99495C16.3819 4.10607 16.4132 4.22759 16.4062 4.35954C16.3993 4.49148 16.3611 4.61995 16.2917 4.74495L14.5 7.59912L16.2917 10.4533C16.3611 10.5783 16.3993 10.7068 16.4062 10.8387C16.4132 10.9706 16.3819 11.0922 16.3125 11.2033C16.2431 11.3144 16.1514 11.4081 16.0375 11.4845C15.9237 11.5609 15.7932 11.5991 15.6458 11.5991H6Z"
                            fill={
                              question && typeof question.flag === 'object' && question.flag?.color
                                ? question.flag.color
                                : '#700108'
                            }
                          />
                        </g>
                      </svg>
                      {question && (typeof question.flag === 'string' ? question.flag : question.flag?.type)}
                    </span>
                    <button className="text-sm text-gray-600 hover:text-gray-800">View less</button>
                  </div>

                  <div
                    className={`p-[14px] rounded ${
                      question &&
                      (typeof question.flag === 'string'
                        ? 'bg-[#EDFFF5]'
                        : (question.flag as { type: string; color: string })?.color === '#10B754'
                          ? 'bg-[#EDFFF5]'
                          : (question.flag as { type: string; color: string })?.color === '#B79035'
                            ? 'bg-[#FFFAED]'
                            : (question.flag as { type: string; color: string })?.color === '#B77235'
                              ? 'bg-[#FFF5ED]'
                              : 'bg-[#FFEDED]')
                    }`}
                  >
                    <p className="text-grey-5 text-body2 sm:text-sm leading-6 tracking-[0.08px]">
                      {question?.remarks}
                    </p>
                  </div>

                  {question?.idealResponse && (
                    <div className="bg-[#DBFFEA80] p-[14px] rounded">
                      <div className="space-y-[4px]">
                        <h4 className="font-semibold text-[#03963F] mb-2">Suggested Improvement</h4>
                        <div className="space-y-4">
                          <p className="text-grey-5 text-body2 sm:text-sm leading-6 tracking-[0.08px]">
                            {typeof question.idealResponse === 'string'
                              ? question.idealResponse
                              : question.idealResponse.description}
                          </p>

                          {typeof question.idealResponse !== 'string' &&
                            question.idealResponse.bestResponse && (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[14px] text-grey-8 font-medium leading-6 tracking-[0.07px]">
                                    💡 Better example:
                                  </span>
                                </div>
                                <div className="flex items-start gap-3 pl-2">
                                  <div className="w-0.5 min-h-16 bg-[#D9D9D9] shrink-0 mt-1"></div>
                                  <p className="text-grey-5 text-body2 sm:text-sm leading-6 tracking-[0.08px] flex-1">
                                    "{question.idealResponse.bestResponse}"
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Transcript

